import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from "react";
import { api } from "@/lib/api";
import { adminApi } from "@/lib/adminApi";

interface User {
  id: string;
  email: string;
  name: string;
  emailVerified: boolean;
  cohort?: string;
  hasPortalAccess?: boolean;
  isAdmin?: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async () => {
      const token = localStorage.getItem("auth_token");
      if (token) {
        try {
          const response = await api.verifyToken();
          if (response.data?.valid && response.data.user) {
            setUser(response.data.user);
          } else {
            // Invalid token, clear it
            api.setToken(null);
            adminApi.setToken(null);
          }
        } catch (error) {
          console.error("Auth check failed:", error);
          api.setToken(null);
          adminApi.setToken(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await api.login(email, password);
    if (response.error) {
      throw new Error(response.error);
    }
    if (response.data?.token && response.data.user) {
      api.setToken(response.data.token);
      adminApi.setToken(response.data.token);
      setUser(response.data.user);
    } else {
      throw new Error("Login failed");
    }
  }, []);

  const register = useCallback(async (email: string, password: string, name: string) => {
    const response = await api.register(email, password, name);
    if (response.error) {
      throw new Error(response.error);
    }
    if (response.data?.token && response.data.user) {
      api.setToken(response.data.token);
      adminApi.setToken(response.data.token);
      setUser(response.data.user);
    } else {
      throw new Error("Registration failed");
    }
  }, []);

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : prev));
  }, []);

  const logout = useCallback(() => {
    api.setToken(null);
    adminApi.setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      register,
      updateUser,
      logout,
      isAuthenticated: !!user,
    }),
    [user, loading, login, register, updateUser, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
